import { useEffect, useRef, useState } from "react";
import { History, Sparkles, X } from "lucide-react";
import { useAskAi } from "../../hooks/useAskAi";
import AskAiComposer from "./AskAiComposer";
import AskAiConversationList from "./AskAiConversationList";
import AskAiMessageList from "./AskAiMessageList";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * AskAiDrawer
 *
 * Purpose: The single Ask AI panel instance for the whole app, mounted
 * once in App.js (not inside AppShell, which remounts per route) and
 * toggled open/closed via AskAiContext so the header trigger on every page
 * can control the same drawer.
 */
function AskAiDrawer() {
  const {
    isOpen,
    closeDrawer,
    config,
    conversations,
    activeConversationId,
    messages,
    loadingConversations,
    loadingMessages,
    sending,
    error,
    fetchConversations,
    selectConversation,
    startNewConversation,
    sendMessage,
    retryMessage,
    cancelMessage,
  } = useAskAi();

  const [showHistory, setShowHistory] = useState(false);
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    previousFocusRef.current = document.activeElement;
    fetchConversations();
    const focusTimer = setTimeout(() => {
      const target = dialogRef.current?.querySelector(FOCUSABLE_SELECTOR);
      target?.focus();
    }, 0);
    return () => clearTimeout(focusTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) return undefined;
    const previous = previousFocusRef.current;
    previousFocusRef.current = null;
    previous?.focus?.();
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeDrawer();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeDrawer]);

  if (!isOpen) return null;

  const handleSend = async (content) => {
    try {
      await sendMessage(content);
    } catch {
      // Failure is already reflected in `messages` (a status:"failed"
      // assistant turn) and `error` — nothing further to do here.
    }
  };

  // Retry re-runs the failed answer in place rather than resending the
  // question as a new message, so it takes a message id, not content.
  const handleRetry = async (messageId) => {
    try {
      await retryMessage(messageId);
    } catch {
      // Same as handleSend: the outcome is already on the message row.
    }
  };

  const handleCancel = async (messageId) => {
    await cancelMessage(messageId);
  };

  const handleSelect = async (conversationId) => {
    setShowHistory(false);
    await selectConversation(conversationId);
  };

  const handleNew = async () => {
    setShowHistory(false);
    await startNewConversation();
  };

  const isUnavailable = config.loaded && (!config.enabled || !config.role_enabled);

  return (
    <div className="fixed inset-0 z-lightbox">
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/40"
        aria-label="Dismiss Ask AI"
        onClick={closeDrawer}
      />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="askai-drawer-title"
        className="fixed inset-y-0 right-0 z-10 flex w-full flex-col border-l border-border bg-surface-raised shadow-2xl sm:w-[378px]"
      >
        <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-accent" aria-hidden="true" />
            <h2 id="askai-drawer-title" className="text-sm font-semibold text-text-primary">
              Ask AI
            </h2>
            <span
              className="rounded-full border border-cyan-500/50 bg-cyan-950/40 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-cyan-300"
              title="Ask AI is still in beta"
            >
              Beta
            </span>
          </div>
          <div className="flex items-center gap-1">
            {!isUnavailable && (
              <button
                type="button"
                onClick={() => setShowHistory((current) => !current)}
                aria-pressed={showHistory}
                aria-label="Conversation history"
                className="flex h-8 w-8 items-center justify-center rounded-md text-text-muted transition hover:bg-surface-sunken hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-surface-raised"
              >
                <History size={16} aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              onClick={closeDrawer}
              aria-label="Close Ask AI"
              className="flex h-8 w-8 items-center justify-center rounded-md text-text-muted transition hover:bg-surface-sunken hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-surface-raised"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </header>

        {isUnavailable ? (
          <div className="flex flex-1 items-center justify-center px-6 text-center" role="status">
            <p className="text-sm text-text-muted">
              {!config.enabled
                ? "Ask AI is currently unavailable. Please try again later."
                : "You do not have access to Ask AI. Contact an administrator if you believe this is a mistake."}
            </p>
          </div>
        ) : (
          <>
            {error && (
              <div
                role="alert"
                className="border-b border-danger/30 bg-danger-surface px-4 py-2 text-xs text-danger"
              >
                {error}
              </div>
            )}
            <div className="flex flex-1 overflow-hidden">
              {showHistory ? (
                <AskAiConversationList
                  conversations={conversations}
                  activeConversationId={activeConversationId}
                  loading={loadingConversations}
                  onSelect={handleSelect}
                  onNew={handleNew}
                />
              ) : (
                <div className="flex flex-1 flex-col overflow-hidden">
                  <AskAiMessageList
                    messages={messages}
                    loading={loadingMessages}
                    sending={sending}
                    onSelectQuestion={handleSend}
                    onRetry={handleRetry}
                    onCancel={handleCancel}
                  />
                  <AskAiComposer
                    onSend={handleSend}
                    disabled={sending}
                    maxChars={config.max_message_chars}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default AskAiDrawer;
